// UTM capture + lightweight click tracking utilities.
// - Captures utm_source / utm_medium / utm_campaign / utm_content / utm_term from the URL.
// - Persists them to sessionStorage so they survive in-app navigation.
// - Appends an attribution suffix to outbound WhatsApp messages so you can
//   eyeball where the lead came from inside WhatsApp itself.
// - Fires events to PostHog (already loaded in public/index.html) and GTM dataLayer
//   if either is available. Falls back to a console.debug log.

import { useEffect, useState } from "react";

const STORAGE_KEY = "shoolin_utm_v1";
const REFERRER_KEY = "shoolin_referrer_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

export function readUtmFromUrl() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const out = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) out[k] = v;
  });
  return out;
}

export function readStoredUtm() {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

export function storeUtm(utm) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
  } catch (_) {
    /* ignore */
  }
}

export function readReferrer() {
  if (typeof window === "undefined") return "";
  try {
    const cached = sessionStorage.getItem(REFERRER_KEY);
    if (cached) return cached;
    const ref = document.referrer || "";
    if (ref) sessionStorage.setItem(REFERRER_KEY, ref);
    return ref;
  } catch (_) {
    return "";
  }
}

/** React hook: captures UTM params on mount, merges with stored, returns the set. */
export function useUtm() {
  const [utm, setUtm] = useState(() => ({ ...readStoredUtm(), ...readUtmFromUrl() }));

  useEffect(() => {
    const fromUrl = readUtmFromUrl();
    if (Object.keys(fromUrl).length) {
      const merged = { ...readStoredUtm(), ...fromUrl };
      storeUtm(merged);
      setUtm(merged);
    }
    // Cache referrer once
    readReferrer();
  }, []);

  return utm;
}

/** Builds a short, human-readable "from: source/campaign" tag for inbound WhatsApp messages. */
export function attributionSuffix(utm = readStoredUtm()) {
  const src = utm.utm_source;
  const cmp = utm.utm_campaign;
  if (!src && !cmp) return "";
  const parts = [];
  if (src) parts.push(src);
  if (cmp && cmp !== src) parts.push(cmp);
  return `\n\n— via ${parts.join(" / ")}`;
}

/** Append attribution to a WhatsApp prefill message. */
export function withAttribution(text) {
  return `${text}${attributionSuffix()}`;
}

/** Fire-and-forget event tracking. Uses PostHog if available, otherwise GTM dataLayer, otherwise console. */
export function trackEvent(name, props = {}) {
  if (typeof window === "undefined") return;
  const merged = {
    ...readStoredUtm(),
    referrer: readReferrer(),
    path: window.location.pathname,
    ...props,
  };
  try {
    if (window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture(name, merged);
    }
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...merged });
    }
    // Debug echo (visible in console only when devtools is open)
    if (window.console && window.console.debug) {
      window.console.debug(`[track] ${name}`, merged);
    }
  } catch (_) {
    /* never throw from tracking */
  }
}
